import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';
import './TableActions.css';

export interface Action {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    className?: string; // e.g., 'btn-edit', 'btn-delete'
    color?: string; // Optional override
    variant?: 'default' | 'danger'; // For mobile styling
    hideLabelDesktop?: boolean;
}

interface TableActionsProps {
    actions: Action[];
}

const TableActions: React.FC<TableActionsProps> = ({ actions }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="table-actions" ref={menuRef}>
            {/* Desktop View: Buttons */}
            <div className="desktop-actions">
                {actions.map((action, index) => (
                    <button
                        key={index}
                        type="button"
                        className={action.className}
                        onClick={(e) => {
                            e.stopPropagation();
                            action.onClick();
                        }}
                        style={action.color ? { color: action.color, background: `${action.color}15` } : undefined}
                        title={action.label}
                    >
                        {action.icon && <span className="btn-icon" style={!action.hideLabelDesktop ? { marginRight: '4px' } : undefined}>{action.icon}</span>}
                        {!action.hideLabelDesktop && action.label}
                    </button>
                ))}
            </div>

            {/* Mobile View: Dropdown */}
            <button
                className="mobile-actions-trigger"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
            >
                <MoreVertical size={20} />
            </button>

            {isOpen && (
                <div className="mobile-actions-menu">
                    {actions.map((action, index) => (
                        <button
                            key={index}
                            className={`mobile-action-item ${action.variant === 'danger' ? 'delete' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsOpen(false);
                                action.onClick();
                            }}
                        >
                            {action.icon}
                            {action.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TableActions;
